import type { Ref } from "lit/directives/ref.js";
import { computePosition, offset, shift, type Placement } from "@floating-ui/dom";
import { html, unsafeCSS, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { SlotController } from "@/common/controllers/slot";
import { EkoElement } from "@/utilities/element";
import style from "./dropdown.css?inline";

@customElement("eko-dropdown")
class Dropdown extends EkoElement {
  static slotPropertyMap = {
    supporting: "supportingElement",
  } as const;

  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private dropdownRef: Ref<HTMLElement> = createRef();

  private handleDocumentClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const path = event.composedPath();

    if (!this.disableClose && !path.includes(this) && !target.id.includes("control-active")) {
      this.hideDropdown();
    }
  };

  private setPosition = () => {
    const triggerElement = this.triggerRef.value!;
    const dropdownElement = this.dropdownRef.value!;

    computePosition(triggerElement, dropdownElement, {
      middleware: [offset(6), shift({ padding: 5 })],
      placement: this.position as Placement,
    }).then(({ x, y }) => {
      Object.assign(dropdownElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  };

  private triggerRef: Ref<HTMLElement> = createRef();

  @property({ reflect: false, type: Boolean }) active = false;

  defaultSlot = new SlotController(this, "default");

  @property({ attribute: "disable-close", reflect: true, type: Boolean }) disableClose = false;

  @property({ reflect: true }) position: Placement = "bottom-start";

  private hideDropdown() {
    this.dropdownRef.value!.classList.remove("eko-dropdown__active");
    this.active = false;
  }

  private showDropdown() {
    this.dropdownRef.value!.classList.add("eko-dropdown__active");
    this.active = true;
    this.setPosition();
  }

  private toggleDropdown() {
    if (this.active) {
      this.hideDropdown();
    } else {
      this.showDropdown();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.setPosition);
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.setPosition);
    super.disconnectedCallback();
    document.removeEventListener("click", this.handleDocumentClick);
  }

  firstUpdated() {
    this.setPosition();
    if (this.active) {
      this.dropdownRef.value!.classList.add("eko-dropdown__active");
    }
  }

  render() {
    return html`
      <slot
        aria-describedby="eko-dropdown"
        name="trigger"
        ${ref(this.triggerRef)}
        @click=${this.toggleDropdown}
        class="eko-dropdown__trigger"></slot>
      <div id="eko-dropdown" class="eko-dropdown" ${ref(this.dropdownRef)} part="dropdown">
        <slot name="header" class="eko-dropdown__header"></slot>
        <slot class="eko-dropdown__body"></slot>
      </div>
    `;
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has("active")) {
      if (this.active) {
        this.showDropdown();
        this.dispatchEvent(new CustomEvent("open", { bubbles: true }));
      } else {
        this.hideDropdown();
        this.dispatchEvent(new CustomEvent("close", { bubbles: true }));
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-dropdown": Dropdown;
  }
}

export default Dropdown;
